import Client from "fhir-kit-client";
import type { Appointment, Bundle, ServiceRequest } from "fhir/r5";
import { ActionPlanAppointmentItem, ActionPlanRequestItem, ActionPlanViewModel } from "../models/ActionPlan";
import { ValueCode } from "../models/OverviewDashboard";
import { getUpCareThemeCode, getUpCareThemeDisplay, UpCareThemeCode } from "../utils/UpCareThemes";
import { getAppointmentParticipationInfo } from "./AppointmentParticipationService";
import LocationService from "./LocationService";

const VALUE_SYSTEM = "https://chunancy.fr/fhir/CodeSystem/upcare-values";
const CONCERNED_ORGANIZATION_EXTENSION = "https://chunancy.fr/fhir/StructureDefinition/upcare-concerned-organization";
const REQUEST_TYPE_CODES = {
    needs: ["besoin", "demande", "demande-atelier-formation", "demande-formation"],
    proposals: ["proposition", "proposition-atelier", "bonne-pratique"]
};

export interface ActionPlanServiceConfig {
    fhirBaseUrl: string;
}

export interface ActionPlanSearchContext {
    value: ValueCode;
    theme?: UpCareThemeCode;
    /** Périmètre des ateliers / formations. */
    organizationReferences: string[];
    /** Périmètre des besoins et solutions. Par défaut, identique au périmètre des ateliers. */
    requestOrganizationReferences?: string[];
    includePastAppointments?: boolean;
}

class ActionPlanService {
    private client: Client;
    private locationService: LocationService;

    constructor(config: ActionPlanServiceConfig) {
        this.client = new Client({ baseUrl: config.fhirBaseUrl });
        this.locationService = new LocationService(config);
    }

    async search(context: ActionPlanSearchContext): Promise<ActionPlanViewModel> {
        const [serviceRequests, appointments, locations] = await Promise.all([
            this.searchServiceRequests(),
            this.searchAppointments(context.includePastAppointments ?? false),
            this.locationService.searchByManagingOrganizations(context.organizationReferences)
        ]);

        const locationReferences = new Set(locations.map((location) => `Location/${location.id}`));
        const appointmentOrganizationReferences = new Set(context.organizationReferences);
        const requestOrganizationReferences = new Set(
            context.requestOrganizationReferences ?? context.organizationReferences
        );

        const relevantRequests = serviceRequests.filter((request) =>
            this.hasValueCode(request, context.value) &&
            (!context.theme || getUpCareThemeCode(request) === context.theme) &&
            this.hasRelevantReference(request, requestOrganizationReferences)
        );

        const needs = relevantRequests
            .filter((request) => this.hasAnyCode(request, REQUEST_TYPE_CODES.needs))
            .map((request) => this.mapRequest(request));

        const proposals = relevantRequests
            .filter((request) => this.hasAnyCode(request, REQUEST_TYPE_CODES.proposals))
            .map((request) => this.mapRequest(request));

        const scheduled = appointments
            .filter((appointment) => this.hasValueCode(appointment, context.value))
            .filter((appointment) => !context.theme || getUpCareThemeCode(appointment) === context.theme)
            .filter((appointment) => this.isRelevantAppointment(
                appointment,
                appointmentOrganizationReferences,
                locationReferences
            ))
            .map((appointment) => this.mapAppointment(appointment));

        if (context.includePastAppointments) {
            scheduled.sort(sortAppointmentsForSummary);
        }

        return { needs, proposals, appointments: scheduled };
    }

    /**
     * Convertit immédiatement un ServiceRequest persisté en modèle Kanban.
     * Utilisé après la création via formulaire pour mettre à jour l'UI sans
     * attendre un nouveau cycle de recherche FHIR.
     */
    mapRequest(request: ServiceRequest): ActionPlanRequestItem {
        const requestAny = request as any;
        const organization = this.getConcernedOrganization(requestAny);
        const themeCode = getUpCareThemeCode(request);

        return {
            id: request.id ?? crypto.randomUUID(),
            title: requestAny.code?.text ??
                requestAny.code?.coding?.[0]?.display ??
                requestAny.code?.concept?.text ??
                requestAny.code?.concept?.coding?.[0]?.display ??
                requestAny.orderDetail?.[0]?.text ??
                "Demande d'accompagnement",
            status: request.status,
            valueCodes: this.collectCodings(request)
                .filter((coding) => coding.system === VALUE_SYSTEM && !!coding.code)
                .map((coding) => coding.code as string),
            themeCode,
            theme: getUpCareThemeDisplay(themeCode),
            organizationReference: organization?.reference,
            organizationDisplay: organization?.display,
            raw: request
        };
    }

    private async searchServiceRequests(): Promise<ServiceRequest[]> {
        const bundle = await this.client.search({
            resourceType: "ServiceRequest",
            searchParams: {
                _count: 100,
                _sort: "-authored"
            }
        }) as Bundle<ServiceRequest>;

        return bundle.entry
            ?.map((entry) => entry.resource)
            .filter((resource): resource is ServiceRequest =>
                !!resource && resource.resourceType === "ServiceRequest"
            )
            .filter((request) => !["revoked", "entered-in-error", "completed"].includes(request.status ?? "")) ?? [];
    }

    private async searchAppointments(includePastAppointments: boolean): Promise<Appointment[]> {
        const bundle = await this.client.search({
            resourceType: "Appointment",
            searchParams: {
                _count: 100,
                _sort: "date"
            }
        }) as Bundle<Appointment>;

        const now = Date.now();

        return bundle.entry
            ?.map((entry) => entry.resource)
            .filter((resource): resource is Appointment =>
                !!resource && resource.resourceType === "Appointment"
            )
            .filter((appointment) => ["proposed", "pending", "booked", "arrived", "fulfilled"].includes(appointment.status ?? ""))
            .filter((appointment) => includePastAppointments || !appointment.end || new Date(appointment.end).getTime() >= now) ?? [];
    }

    private hasValueCode(resource: unknown, value: ValueCode): boolean {
        return this.collectCodings(resource).some((coding) =>
            coding.code === value && (!coding.system || coding.system === VALUE_SYSTEM)
        );
    }

    private hasAnyCode(resource: unknown, codes: string[]): boolean {
        return this.collectCodings(resource).some((coding) =>
            !!coding.code && codes.includes(coding.code)
        );
    }

    private collectCodings(resource: unknown): Array<{ system?: string; code?: string; display?: string }> {
        const result: Array<{ system?: string; code?: string; display?: string }> = [];
        const visit = (value: unknown) => {
            if (!value || typeof value !== "object") return;
            if (Array.isArray(value)) {
                value.forEach(visit);
                return;
            }
            const object = value as Record<string, unknown>;
            if (typeof object.code === "string") {
                result.push({
                    system: typeof object.system === "string" ? object.system : undefined,
                    code: object.code,
                    display: typeof object.display === "string" ? object.display : undefined
                });
            }
            Object.values(object).forEach(visit);
        };
        visit(resource);
        return result;
    }

    private hasRelevantReference(resource: unknown, references: Set<string>): boolean {
        let found = false;
        const visit = (value: unknown) => {
            if (found || !value || typeof value !== "object") return;
            if (Array.isArray(value)) {
                value.forEach(visit);
                return;
            }
            const object = value as Record<string, unknown>;
            if (typeof object.reference === "string" && references.has(object.reference)) {
                found = true;
                return;
            }
            Object.values(object).forEach(visit);
        };
        visit(resource);
        return found;
    }

    private isRelevantAppointment(
        appointment: Appointment,
        organizationReferences: Set<string>,
        locationReferences: Set<string>
    ): boolean {
        return appointment.participant?.some((participant) => {
            const reference = participant.actor?.reference;
            return !!reference && (
                organizationReferences.has(reference) ||
                locationReferences.has(reference)
            );
        }) ?? false;
    }

    private getConcernedOrganization(request: any): { reference?: string; display?: string } | undefined {
        const extensionReference = request.extension
            ?.find((extension: any) => extension?.url === CONCERNED_ORGANIZATION_EXTENSION)
            ?.valueReference;

        if (extensionReference?.reference || extensionReference?.display) {
            return extensionReference;
        }

        if (request.requester?.reference?.startsWith("Organization/")) {
            return request.requester;
        }

        return undefined;
    }

    private mapAppointment(appointment: Appointment): ActionPlanAppointmentItem {
        const appointmentAny = appointment as any;
        const location = appointment.participant
            ?.map((participant) => participant.actor)
            .find((actor) => actor?.reference?.startsWith("Location/"));
        const presenter = appointment.participant
            ?.find((participant) => participant.type?.some((type) =>
                type.coding?.some((coding) => coding.code === "PPRF")
            ))?.actor?.display;
        const participation = getAppointmentParticipationInfo(appointment);
        const comment = typeof appointmentAny.comment === "string" ? appointmentAny.comment : "";
        const details = this.parseAppointmentComment(comment);
        const themeCode = getUpCareThemeCode(appointment);

        return {
            id: appointment.id ?? crypto.randomUUID(),
            title: appointment.description ?? "Atelier / formation",
            start: appointment.start,
            end: appointment.end,
            location: location?.display,
            type: appointmentAny.appointmentType?.text ?? appointmentAny.appointmentType?.coding?.[0]?.display,
            presenter,
            participantCount: participation.participantCount,
            participantCapacity: participation.participantCapacity,
            isRegistered: participation.isRegistered,
            objectives: details.objectives,
            schedule: details.schedule,
            valueCodes: this.collectCodings(appointment)
                .filter((coding) => coding.system === VALUE_SYSTEM && !!coding.code)
                .map((coding) => coding.code as string),
            themeCode,
            theme: getUpCareThemeDisplay(themeCode),
            raw: appointment
        };
    }

    private parseAppointmentComment(comment: string): { objectives: string[]; schedule?: string } {
        if (!comment.trim()) {
            return { objectives: [] };
        }

        const normalized = comment.replace(/\r/g, "").trim();
        const objectiveMatch = normalized.match(/Objectifs?\s*:\s*([\s\S]*?)(?=\n?\s*Déroulé\s*:|$)/i);
        const scheduleMatch = normalized.match(/Déroulé\s*:\s*([\s\S]*)$/i);
        const objectiveText = objectiveMatch?.[1]?.trim() ?? "";

        return {
            objectives: objectiveText
                ? objectiveText
                    .split(/\n|;|•|\u2022/)
                    .map((item) => item.replace(/^[-–—]\s*/, "").trim())
                    .filter(Boolean)
                : [],
            schedule: scheduleMatch?.[1]?.trim() || undefined
        };
    }
}

const sortAppointmentsForSummary = (
    left: ActionPlanAppointmentItem,
    right: ActionPlanAppointmentItem
): number => {
    const now = Date.now();
    const leftStart = toTimestamp(left.start);
    const rightStart = toTimestamp(right.start);
    const leftEnd = toTimestamp(left.end ?? left.start);
    const rightEnd = toTimestamp(right.end ?? right.start);
    const leftUpcoming = leftEnd >= now;
    const rightUpcoming = rightEnd >= now;

    if (leftUpcoming !== rightUpcoming) {
        return leftUpcoming ? -1 : 1;
    }

    if (leftUpcoming) {
        return leftStart - rightStart;
    }

    return rightStart - leftStart;
};

const toTimestamp = (value?: string): number => {
    if (!value) return 0;
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
};

export default ActionPlanService;
